import { useState, useEffect } from "react";
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress, 
  useTheme,
  Grid,
  TextField
} from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";

// أيقونات
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import axios from "axios";
import StatCard from "../../components/StatCard"
import DataTable from "../../components/DataTable";
import InventoryTable from "../../components/InventoryTable";
import { useTranslation } from 'react-i18next';
const apiUrl = "https://oven-backend.onrender.com";

// خدمات البيانات
const fetchSalesData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/sales`);
    return {
      paid: data.reduce((sum, sale) => sum + (sale.payment_status === 'paid' ? sale.total_amount : 0), 0),
      unpaid: data.reduce((sum, sale) => sum + (sale.payment_status === 'unpaid' ? sale.total_amount : 0), 0)
    };
  } catch (error) {
    console.error("Failed to fetch sales:", error);
    return { paid: 0, unpaid: 0 };
  }
};

const fetchExpensesData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/expenses`);
    return {
      paid: data.reduce((sum, expense) => sum + (expense.payment_status === 'paid' ? expense.amount : 0), 0),
      unpaid: data.reduce((sum, expense) => sum + (expense.payment_status === 'unpaid' ? expense.amount : 0), 0)
    };
  } catch (error) {
    console.error("Failed to fetch expenses:", error);
    return { paid: 0, unpaid: 0 };
  }
};

const fetchSalariesData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/employees`);
    return data
      .filter(emp => emp.role === 'EMPLOYEE')
      .reduce((sum, emp) => sum + (emp.salary || 0), 0);
  } catch (error) {
    console.error("Failed to fetch salaries:", error);
    return 0;
  }
};

const fetchInventoryData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/inventory`);
    return data.map(item => ({
      ...item,
      status: item.totalQuantity < item.minStockLevel ? 'low' : 'good'
    }));
  } catch (error) {
    console.error("Failed to fetch inventory:", error);
    return [];
  }
};

const fetchPurchasesData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/dailyConsumption`);
    return data.reduce((sum, purchase) => sum + purchase.consumedAmount * purchase.item.unitPrice, 0);
  } catch (error) {
    console.error("Failed to fetch purchases:", error);
    return 0;
  }
};
const fetchConsumptionData = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/dailyConsumption`);
    return data;
  } catch (error) {
    console.error("Failed to fetch consumption data:", error);
    return [];
  }
};


const calculateInventoryByDate = (inventory, consumption, selectedDate) => {
  return inventory.map(item => {
    const totalConsumed = consumption
      .filter(cons => cons.item._id === item._id && new Date(cons.consumptionDate) <= new Date(selectedDate))
      .reduce((sum, cons) => sum + cons.consumedAmount, 0);

    const currentQuantity = item.totalQuantity - totalConsumed;

    return {
      ...item,
      currentQuantity,
      status: currentQuantity < item.minStockLevel ? 'low' : 'good'
    };
  });
};

const FinancialReports = () => {
  const { t, i18n } = useTranslation();
  console.log("Current Language:", i18n.language);
  console.log(t("dashboard"))
  // useEffect(() => {
  //   i18n.changeLanguage("ar");
  //   localStorage.removeItem("i18nextLng"); // تغيير اللغة إلى العربية تلقائيًا
  // }, []);

    const isRTL = i18n.language === "ar";
  const [reportData, setReportData] = useState({
    sales: { paid: 0, unpaid: 0 },
    expenses: 0,
    salaries: 0,
    purchases: 0,
    inventory: [],
    capital: 0
  });
  
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedDate, setSelectedDate] = useState("");
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [consumption, setConsumption] = useState([]);
  useEffect(() => {
    const loadData = async () => {
      try {
        const [sales, expenses, salaries, inventory, purchases, consumption] = await Promise.all([
          fetchSalesData(),
          fetchExpensesData(),
          fetchSalariesData(),
          fetchInventoryData(),
          fetchPurchasesData(),
          fetchConsumptionData() // ✅ تحميل بيانات الاستهلاك
        ]);
  
        const inventoryValue = inventory.reduce((sum, item) => 
          sum + (item.totalQuantity * item.unitPrice), 0);
  
        const assets = sales.paid + inventoryValue;
        const liabilities = expenses.unpaid + salaries + purchases;
        const capital = assets - liabilities;
  
        setInventory(inventory);
        setConsumption(consumption); // ✅ تعيين بيانات الاستهلاك
        setReportData({
          sales,
          expenses,
          salaries,
          purchases,
          inventory,
          capital
        });
      } catch (error) {
        console.error("Failed to load report data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    loadData();
  }, []);
  

  useEffect(() => {
    if (selectedDate) {
      const updatedInventory = calculateInventoryByDate(inventory, consumption, selectedDate);
      setFilteredInventory(updatedInventory);
    }
  }, [selectedDate, inventory, consumption]);
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
<Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr" }}> 
       <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
       <Header title={t("dashboard")} subtitle={t("dashboard")} />
      </Box>

      <Grid container spacing={3}>

        {/* المبيعات المدفوعة */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title={t("sales_paid")} 
            value={reportData.sales.paid}
            icon={<PointOfSaleIcon />}
            color={colors.blueAccent[500]}
            formatCurrency
          />
        </Grid>

        {/* المبيعات غير المدفوعة */}
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title={t("sales_unpaid")}        
            value={reportData.sales.unpaid}
            icon={<MoneyOffIcon />}
            color={colors.redAccent[500]}
            formatCurrency
          />
        </Grid>

      {/* المصروفات المدفوعة */}
<Grid item xs={12} md={6} lg={3}>
  <StatCard
    title={t("expenses_paid")}
    value={reportData.expenses.paid}
    icon={<AttachMoneyIcon />}
    color={colors.blueAccent[500]}
    formatCurrency
  />
</Grid>

{/* المصروفات غير المدفوعة */}
<Grid item xs={12} md={6} lg={3}>
  <StatCard
     title={t("expenses_unpaid")}
    value={reportData.expenses.unpaid}
    icon={<MoneyOffIcon />}
    color={colors.redAccent[500]}
    formatCurrency
  />
</Grid>

        {/* مخزون المواد */}
        <Box m="20px" width="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Header title={t("inventory_control")} subtitle={t("inventory_analysis")} />
          <TextField type="date" label={t("select_date")} InputLabelProps={{ shrink: true }} />
        </Box>

      <Grid container spacing={3}>
      <Grid item xs={12}>
            <InventoryTable
            data={reportData.inventory}
            columns={[
                { field: "itemName", headerName: t("item_name"), width: 200 },
                { field: "currentQuantity", headerName: t("available_quantity"), width: 150 },
                { field: "minStockLevel", headerName: t("minimum_stock"), width: 150 },
                {
                  field: "status",
                  headerName: t("stock_status"),
                  width: 200,
                  renderCell: (params) => (
                    <Box sx={{ color: params.value === "low" ? "red" : "green", fontWeight: "bold" }}>
                      {params.value === "low" ? t("below_minimum") : t("good_level")}
                    </Box>
                  )
                }
              ]}
              title={t("updated_inventory")}
              colors={colors}
              />
          </Grid>
      </Grid>
    </Box>
      </Grid>
    </Box>
  );
};

export default FinancialReports;